import 'react-datepicker/dist/react-datepicker.css';

import { Card, Col, Input, Row, Statistic, Table, Tag, Typography } from 'antd';
import DatePicker, { registerLocale } from 'react-datepicker';
import {
  FaArrowDown,
  FaCalendarAlt,
  FaChartLine,
  FaCheckCircle,
  FaClock,
  FaMoneyBillWave,
  FaSearch,
  FaTimesCircle,
  FaUsers,
  FaWallet,
} from 'react-icons/fa';
import { useCallback, useEffect, useState } from 'react';

import { DepositGrid } from '~/data/dummy';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { RangeDatePicker } from '~/components/common';
import axios from 'axios';
import { baseUrl } from '~/utils/helper';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import vi from 'date-fns/locale/vi';
import { useStateContext } from '~/contexts/ContextProvider';

const { Search } = Input;
const { Text } = Typography;
registerLocale('vi', vi);

const Withdraw = ({ title }) => {
  const { formatMoney, ref } = useStateContext();
  const { t } = useTranslation('report');
  const [startDate, setStartDate] = useState(
    dayjs().add(-7, 'd').format('YYYY-MM-DD') + ' 00:00:00',
  );
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD') + ' 23:59:59');
  const [dataUser, setDataUser] = useState([]);
  const [searchVal, setSearchVal] = useState('');
  const [isLoad, setIsLoad] = useState(true);
  const [infoWithdraw, setInfoWithdraw] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  document.title = title;

  // Fetch data function
  const fetchData = useCallback(
    (page = 1, username = '') => {
      setIsLoad(true);
      axios
        .get(`${baseUrl}/api/daily/withdraw/list/all`, {
          params: {
            start: dayjs(startDate).format('YYYY-MM-DD') + ' 00:00:00',
            end: dayjs(endDate).format('YYYY-MM-DD') + ' 23:59:59',
            page: page,
            pageSize: pagination.pageSize,
            username: username.trim(),
          },
          headers: {
            'x-access-token': sessionStorage.getItem('auth'),
            'Access-Control-Allow-Origin': '*',
          },
        })
        .then((res) => {
          const { status, data, totalChoXuLy, totalThanhCong, totalThatBai, total, totalDocs } =
            res.data;
          if (status) {
            setInfoWithdraw({
              total,
              totalChoXuLy,
              totalThanhCong,
              totalThatBai,
            });
            setDataUser(data);
            setPagination((prev) => ({
              ...prev,
              current: page,
              total: totalDocs || data?.length || 0,
            }));
            setIsLoad(false);
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error(t('common:error'), { theme: 'light' });
          setIsLoad(false);
        });
    },
    [startDate, endDate, pagination.pageSize, t],
  );

  useEffect(() => {
    fetchData(1, searchVal);
  }, [startDate, endDate]);

  const handleSearch = () => {
    // Reset to page 1 when searching
    fetchData(1, searchVal);
  };

  const handleTableChange = (paginationConfig) => {
    const { current, pageSize } = paginationConfig;

    // Update pagination state
    setPagination((prev) => ({
      ...prev,
      current: current,
      pageSize: pageSize,
    }));

    // Fetch data for new page
    fetchData(current, searchVal);
  };

  const handleChangeDate = (v) => {
    setStartDate(v[0]);
    setEndDate(v[1]);
  };

  // Handle search input with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchVal(value);
  };

  // Handle search button click or Enter key
  const handleSearchSubmit = () => {
    fetchData(1, searchVal);
  };

  const depositGrid = DepositGrid(false);

  return (
    <div className="min-h-screen p-2 md:p-6 bg-gradient-to-br from-slate-50 via-red-50 to-pink-100 ">
      {/* Header Section */}
      <div className="p-2 mb-4 border shadow-xl md:mb-8 md:p-6 rounded-2xl bg-white/90 backdrop-blur-2xl border-white/20">
        <div className="flex flex-col justify-between gap-3 md:flex-row">
          {/* Title */}
          <div>
            <h1 className="flex items-center text-2xl font-bold text-center text-transparent bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text md:text-left md:text-3xl md:mb-2">
              <FaArrowDown className="inline mr-3 text-red-500" />
              {t('lich_su_rut_tien')}
            </h1>
            <p className="hidden text-gray-600 md:block">{t('ql_va_td_hdrt')}</p>
          </div>

          {/* Controls Row */}
          <div className="flex flex-col gap-2 lg:flex-row md:gap-4 lg:items-end">
            {/* Date Range */}
            <div className="flex flex-col gap-2">
              <label className="items-center hidden gap-2 text-sm font-medium text-gray-700 md:flex">
                <FaCalendarAlt className="text-blue-500" />
                {t('khoang_thoi_gian')}
              </label>
              <RangeDatePicker
                onChange={handleChangeDate}
                className="w-full h-10 border-2 rounded-lg md:h-12 border-red-500/20 bg-white/90"
              />
            </div>

            {/* Username Search */}
            <div className="flex justify-between gap-2 md:flex-col md:justify-center">
              <label className="items-center hidden gap-2 text-sm font-medium text-gray-700 md:flex">
                <FaUsers className="text-purple-500" />
                {t('tim_kiem_nguoi_choi')}
              </label>
              <Input
                value={searchVal}
                onChange={handleSearchChange}
                onPressEnter={handleSearchSubmit}
                placeholder={t('nhap_ten_dang_nhap')}
                className="md:w-[220px] w-3/5 rounded-lg md:h-12 h-10"
                allowClear
              />
              <button
                onClick={handleSearch}
                className="flex items-center justify-center h-10 gap-2 px-3 py-3 font-semibold text-white transition-all duration-300 border-none shadow-lg md:hidden md:h-12 rounded-xl bg-gradient-to-r from-red-500 to-pink-600 hover:scale-105 hover:shadow-xl">
                <FaSearch size={16} />
                {t('common:search')}
              </button>
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              className="items-center justify-center hidden h-10 gap-2 px-6 py-3 font-semibold text-white transition-all duration-300 border-none shadow-lg md:flex md:h-12 rounded-xl bg-gradient-to-r from-red-500 to-pink-600 hover:scale-105 hover:shadow-xl">
              <FaSearch size={16} />
              {t('common:search')}
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} className="mb-4 md:mb-8">
        <Col xs={12} sm={12} lg={6}>
          <Card className="text-center bg-gradient-to-r from-blue-500 to-blue-600 border-none rounded-2xl text-white hover:scale-105 transition-all duration-300 md:[&_.ant-card-body]:!p-[24px] [&_.ant-card-body]:!p-[12px]">
            <Statistic
              title={
                <span className="flex items-center justify-center gap-2 text-white/90">
                  <FaUsers /> {t('tong_giao_dich')}
                </span>
              }
              value={formatMoney(infoWithdraw.total)}
              valueStyle={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card className="text-center bg-gradient-to-r from-green-500 to-emerald-600 border-none rounded-2xl text-white hover:scale-105 transition-all duration-300 md:[&_.ant-card-body]:!p-[24px] [&_.ant-card-body]:!p-[12px]">
            <Statistic
              title={
                <span className="flex items-center justify-center gap-2 text-white/90">
                  <FaCheckCircle /> {t('thanh_cong')}
                </span>
              }
              value={formatMoney(infoWithdraw.totalThanhCong)}
              valueStyle={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
            />
          </Card>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <Card className="text-center bg-gradient-to-r from-yellow-500 to-orange-500 border-none rounded-2xl text-white hover:scale-105 transition-all duration-300 md:[&_.ant-card-body]:!p-[24px] [&_.ant-card-body]:!p-[12px]">
            <Statistic
              title={
                <span className="flex items-center justify-center gap-2 text-white/90">
                  <FaClock /> {t('cho_xu_ly')}
                </span>
              }
              value={formatMoney(infoWithdraw.totalChoXuLy)}
              valueStyle={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
            />
          </Card>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <Card className="text-center bg-gradient-to-r from-red-500 to-red-600 border-none rounded-2xl text-white hover:scale-105 transition-all duration-300 md:[&_.ant-card-body]:!p-[24px] [&_.ant-card-body]:!p-[12px]">
            <Statistic
              title={
                <span className="flex items-center justify-center gap-2 text-white/90">
                  <FaTimesCircle /> {t('that_bai')}
                </span>
              }
              value={formatMoney(infoWithdraw.totalThatBai)}
              valueStyle={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Data Table Section */}
      <div className="p-2 border shadow-xl md:p-6 rounded-2xl bg-white/90 backdrop-blur-2xl border-white/20">
        <Table
          rowKey="id"
          dataSource={dataUser}
          columns={depositGrid}
          loading={isLoad}
          bordered={false}
          className="overflow-hidden custom-withdraw-table rounded-xl"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ${t('cuar')} ${total} ${t('giao_dich')}`,
            className: 'mt-6',
            onChange: (page, pageSize) => {
              handleTableChange({ current: page, pageSize });
            },
            onShowSizeChange: (current, size) => {
              handleTableChange({ current: 1, pageSize: size });
            },
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          rowClassName={(record, index) => {
            let statusClass = '';
            if (record.status === 3) statusClass = 'border-l-4 border-l-green-500';
            else if (record.status === 0 || record.status === 1)
              statusClass = 'border-l-4 border-l-yellow-500';
            else if (record.status === 2 || record.status === 4)
              statusClass = 'border-l-4 border-l-red-500';

            return `${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-red-50 ${statusClass}`;
          }}
          summary={(pageData) => {
            let amount = pageData.reduce((pre, current) => {
              const amount = current.status == 3 ? current.amount : 0;
              return pre + (Number(amount) || 0);
            }, 0);

            return (
              <Table.Summary.Row className="font-semibold text-white bg-gradient-to-r from-red-500 to-pink-600">
                <Table.Summary.Cell className="text-center" colSpan={2}>
                  <span className="flex items-center justify-center gap-2 text-lg">
                    <FaArrowDown />
                    {t('tong_trang_hien_tai')}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell className="text-center">
                  <Text className="text-lg font-bold text-white">{formatMoney(amount)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell colSpan={3}></Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </div>

      <style jsx>{`
        .custom-withdraw-table .ant-table-thead > tr > th {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
          border-bottom: 2px solid #e2e8f0 !important;
          font-weight: 600 !important;
          color: #374151 !important;
          padding: 16px !important;
          text-align: center !important;
        }

        .custom-withdraw-table .ant-table-tbody > tr > td {
          padding: 12px 16px !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }

        .ant-pagination .ant-pagination-item {
          border-radius: 8px !important;
          border: 1px solid #e2e8f0 !important;
        }

        .ant-pagination .ant-pagination-item-active {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
          border-color: transparent !important;
        }

        .ant-pagination .ant-pagination-item-active a {
          color: white !important;
        }

        .ant-table-summary tr td {
          border-top: 2px solid #dc2626 !important;
        }
      `}</style>
    </div>
  );
};

export default Withdraw;
